/**
 * OrganizationService - dodatni podaci organizacije (kontakt osoba).
 */

import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';

/**
 * @param {object} data
 * @returns {{ first_name: string|null, last_name: string|null, phone: string|null, email: string|null }}
 */
export function normalizeOrganizationAdditionalInfo(data = {}) {
  const trimOrNull = (v) => {
    const s = typeof v === 'string' ? v.trim() : '';
    return s || null;
  };

  return {
    first_name: trimOrNull(
      data.first_name ?? data.firstName ?? data.contactFirstName
    ),
    last_name: trimOrNull(
      data.last_name ?? data.lastName ?? data.contactLastName
    ),
    phone: trimOrNull(data.phone ?? data.userPhone),
    email: trimOrNull(data.email ?? data.userEmail),
  };
}

/**
 * BE SaveOrUpdateAdditionalOrgInfoReq / SearchOrgAdditionalInfoReq (snake_case u JSON-u).
 * @param {string} organizationId
 * @param {object} data
 * @returns {{ organization_id: string, first_name: string|null, last_name: string|null, phone: string|null, email: string|null }}
 */
export function toAdditionalInfoApiBody(organizationId, data = {}) {
  const contact = normalizeOrganizationAdditionalInfo(data);
  return {
    organization_id: organizationId,
    first_name: contact.first_name,
    last_name: contact.last_name,
    phone: contact.phone,
    email: contact.email,
  };
}

/**
 * @param {unknown} response
 * @returns {{ first_name: string, last_name: string, phone: string, email: string }}
 */
export function parseAdditionalInfoResponse(response) {
  const raw = response?.data ?? response;
  const item = Array.isArray(raw) ? raw[0] : raw;
  if (!item || typeof item !== 'object') {
    return { first_name: '', last_name: '', phone: '', email: '' };
  }
  return {
    first_name: item.first_name ?? item.firstName ?? '',
    last_name: item.last_name ?? item.lastName ?? '',
    phone: item.phone ?? '',
    email: item.email ?? '',
  };
}

class OrganizationService {
  /**
   * @param {string} organizationId
   * @returns {Promise<{ first_name: string, last_name: string, phone: string, email: string }>}
   */
  async searchAdditionalInfo(organizationId) {
    if (!organizationId) {
      throw new Error('ID organizacije je obavezan');
    }

    try {
      const response = await ApiService.post(
        API_ENDPOINTS.ORGANIZATION.ADDITIONAL_INFO_SEARCH,
        { organization_id: organizationId }
      );
      return parseAdditionalInfoResponse(response);
    } catch (error) {
      const status = error.response?.status;
      const message = typeof error.message === 'string' ? error.message : '';
      const isNotFound =
        status === 404 ||
        message.includes('404') ||
        message.toLowerCase().includes('no additional info found');

      if (isNotFound) {
        return { first_name: '', last_name: '', phone: '', email: '' };
      }

      if (status === 401) {
        throw new Error('Korisnik nije prijavljen');
      }
      throw error;
    }
  }

  /**
   * @param {{ organization_id: string, first_name?: string|null, last_name?: string|null, phone?: string|null, email?: string|null }} payload
   */
  async saveAdditionalInfo(payload) {
    const organizationId = payload?.organization_id ?? payload?.organizationId;
    if (!organizationId) {
      throw new Error('ID organizacije je obavezan');
    }

    const body = toAdditionalInfoApiBody(organizationId, payload);

    try {
      const response = await ApiService.post(
        API_ENDPOINTS.ORGANIZATION.SAVE_ADDITIONAL_INFO,
        body
      );
      return response.data ?? response;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Korisnik nije prijavljen');
      }
      if (error.response?.status === 400) {
        throw new Error(
          error.response.data?.message || 'Neispravni kontakt podaci organizacije'
        );
      }
      throw error;
    }
  }
}

const organizationServiceInstance = new OrganizationService();
export default organizationServiceInstance;
